import type { LucideIcon } from 'lucide-react';
import {
  AtSign, BellRing, CalendarClock, CalendarDays, CalendarPlus, CalendarX, CheckSquare,
  CornerUpLeft, FileText, Megaphone, MessageSquare, MessageSquareQuote, PartyPopper,
  Phone, PhoneMissed, Settings, ShieldAlert, SmilePlus, UserPlus, Users, Video, Voicemail, Vote,
} from 'lucide-react';
import type { NotificationKind } from './types';

export interface KindMeta {
  label: string;
  icon: LucideIcon;
  /** Tailwind text colour for the glyph tile — semantic tokens only. */
  tone: string;
}

export const KIND_META: Record<NotificationKind, KindMeta> = {
  message: { label: 'Message', icon: MessageSquare, tone: 'text-primary' },
  mention: { label: 'Mention', icon: AtSign, tone: 'text-primary' },
  reply: { label: 'Reply', icon: CornerUpLeft, tone: 'text-primary' },
  reaction: { label: 'Reaction', icon: SmilePlus, tone: 'text-warning' },
  channelPost: { label: 'Channel post', icon: MessageSquareQuote, tone: 'text-info' },
  announcement: { label: 'Announcement', icon: Megaphone, tone: 'text-success' },
  callIncoming: { label: 'Incoming call', icon: Phone, tone: 'text-success' },
  callMissed: { label: 'Missed call', icon: PhoneMissed, tone: 'text-destructive' },
  voicemail: { label: 'Voicemail', icon: Voicemail, tone: 'text-info' },
  meetingInvite: { label: 'Meeting invite', icon: CalendarPlus, tone: 'text-info' },
  meetingReminder: { label: 'Meeting reminder', icon: CalendarClock, tone: 'text-warning' },
  meetingStarted: { label: 'Meeting started', icon: Video, tone: 'text-primary' },
  meetingChanged: { label: 'Meeting updated', icon: CalendarX, tone: 'text-warning' },
  fileShared: { label: 'File shared', icon: FileText, tone: 'text-info' },
  fileComment: { label: 'File comment', icon: MessageSquareQuote, tone: 'text-info' },
  taskAssigned: { label: 'Task assigned', icon: CheckSquare, tone: 'text-success' },
  taskUpdated: { label: 'Task updated', icon: CheckSquare, tone: 'text-info' },
  taskComment: { label: 'Task comment', icon: MessageSquareQuote, tone: 'text-info' },
  taskDue: { label: 'Task due', icon: CalendarClock, tone: 'text-warning' },
  approvalRequest: { label: 'Approval', icon: ShieldAlert, tone: 'text-warning' },
  teamInvite: { label: 'Team invite', icon: Users, tone: 'text-primary' },
  joinRequest: { label: 'Join request', icon: UserPlus, tone: 'text-primary' },
  praise: { label: 'Praise', icon: PartyPopper, tone: 'text-success' },
  poll: { label: 'Poll', icon: Vote, tone: 'text-warning' },
  system: { label: 'System', icon: BellRing, tone: 'text-muted-foreground' },
  admin: { label: 'Admin', icon: Settings, tone: 'text-muted-foreground' },
};

/** Filter tabs in the notification centre. */
export const KIND_FILTERS = {
  all: null,
  unread: null,
  mentions: ['mention', 'reply'] as const,
  calls: ['callIncoming', 'callMissed', 'voicemail'] as const,
  meetings: ['meetingInvite', 'meetingReminder', 'meetingStarted', 'meetingChanged'] as const,
  tasks: ['taskAssigned', 'taskUpdated', 'taskComment', 'taskDue'] as const,
} as const;

export type NotificationFilter = keyof typeof KIND_FILTERS;

export const FILTER_LABEL: Record<NotificationFilter, string> = {
  all: 'All',
  unread: 'Unread',
  mentions: 'Mentions',
  calls: 'Calls',
  meetings: 'Meetings',
  tasks: 'Tasks',
};

export const CALENDAR_GLYPH: LucideIcon = CalendarDays;
